import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api-client';

/**
 * Wire-shape mirror of the backend `UserHighlightsDto` returned by
 * `GET /api/users/me/highlights` (commit 1f545cb). Inline here per
 * the codebase convention for hook-local response types
 * (`EntitlementResult` in `useEntitlement.ts`, etc.).
 */
export interface UserHighlightsChildDto {
  childId: string;
  childName: string;
  storyCount: number;
  levelLabel: string;
  levelNumber: number;
}

export interface UserHighlightsDto {
  children: UserHighlightsChildDto[];
  totalStories: number;
  favoriteWorld: string | null;
  hideCard: boolean;
}

/**
 * Fetch the "Eure Heldenchronik" Start-Page card payload (F.4).
 *
 * Locked behavior (Sebastian Gate-0 2026-05-08):
 *   - Loading: silent — returns null until data resolves.
 *   - Error: silent fail — returns null on fetch failure (Sentry
 *     catches via apiFetch's normal flow). No error UI.
 *   - hideCard: when the API returns hideCard=true, the hook
 *     resolves to null so the card doesn't render. Caller doesn't
 *     need to inspect hideCard.
 *   - Refetch: on-mount only. No SWR cache, no manual refetch.
 *
 * Pattern: simple `useState` + `useEffect` (not the module-level
 * cache pattern used by `useEntitlement` / `useStoryCount` —
 * overkill for a single Start-Page card with no cross-page reuse).
 */
export function useHighlights(): UserHighlightsDto | null {
  const [data, setData] = useState<UserHighlightsDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<UserHighlightsDto>('/api/users/me/highlights')
      .then((result) => {
        if (cancelled) return;
        if (result.hideCard) {
          setData(null);
          return;
        }
        setData(result);
      })
      .catch(() => {
        // Silent fail — card hides on error. apiFetch already
        // captures the error via the normal error pipeline.
        if (cancelled) return;
        setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}
